import React, { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Alert,
    Button,
    LinearProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    ExpandMore as ExpandMoreIcon,
    TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { liveAnalyticsAPI } from '../../services/liveAnalyticsApi';

const EnrollmentDataQuality: React.FC = () => {
    const [forceRefresh, setForceRefresh] = useState(false);

    const { data: qualityData, isLoading, error, refetch } = useQuery({
        queryKey: ['enrollmentDataQuality', forceRefresh],
        queryFn: () => liveAnalyticsAPI.getEnrollmentDataQuality(forceRefresh),
        refetchInterval: 300000, // Refetch every 5 minutes
    });

    const handleRefresh = () => {
        setForceRefresh(true);
        refetch().then(() => setForceRefresh(false));
    };

    const getScoreColor = (score: number) => {
        if (score >= 90) return 'success';
        if (score >= 80) return 'info';
        if (score >= 70) return 'warning';
        if (score >= 60) return 'warning';
        return 'error';
    };

    const getGradeColor = (grade: string) => {
        if (grade === 'A') return '#4caf50';
        if (grade === 'B') return '#2196f3';
        if (grade === 'C') return '#ff9800';
        if (grade === 'D') return '#ff9800';
        return '#f44336';
    };

    const getPriorityIcon = (priority: string) => {
        if (priority === 'high') return <ErrorIcon color="error" />;
        if (priority === 'medium') return <WarningIcon color="warning" />;
        return <CheckIcon color="success" />;
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load data quality report
                </Alert>
                <Button variant="contained" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                    Retry
                </Button>
            </Box>
        );
    }

    const report = qualityData?.data?.data;
    if (!report) {
        return <Alert severity="info">No data quality report available</Alert>;
    }

    const overallScore = report.overall_score || 0;
    const summary = report.summary || {};
    const checks = report.checks || {};

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Enrollment Data Quality
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        Comprehensive quality assessment of enrollment data
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                >
                    Refresh
                </Button>
            </Box>

            {/* Overall Score Card */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${getGradeColor(summary.overall_grade)} 0%, ${getGradeColor(summary.overall_grade)}dd 100%)` }}>
                        <CardContent sx={{ textAlign: 'center', color: 'white' }}>
                            <Typography variant="h6" gutterBottom>
                                Overall Quality Score
                            </Typography>
                            <Typography variant="h1" sx={{ fontWeight: 'bold', my: 2 }}>
                                {overallScore.toFixed(1)}
                            </Typography>
                            <Chip
                                label={`Grade: ${summary.overall_grade}`}
                                sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 'bold', fontSize: '1.2rem' }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Summary
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h3" color="primary">
                                            {summary.total_checks || 0}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Total Checks
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h3" color="success.main">
                                            {summary.passed_checks || 0}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Passed
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h3" color="error.main">
                                            {summary.failed_checks || 0}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Failed
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h3" color="warning.main">
                                            {summary.total_issues || 0}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Issues
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Quality Checks */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Quality Dimensions
                    </Typography>
                    <Grid container spacing={2}>
                        {Object.entries(checks).map(([category, data]: [string, any]) => (
                            <Grid item xs={12} sm={6} md={4} key={category}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                                                {category}
                                            </Typography>
                                            <Chip
                                                label={`${data.score.toFixed(0)}%`}
                                                color={getScoreColor(data.score)}
                                                size="small"
                                            />
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={data.score}
                                            color={getScoreColor(data.score)}
                                            sx={{ height: 8, borderRadius: 4, mb: 1 }}
                                        />
                                        {data.issues && data.issues.length > 0 ? (
                                            <Box>
                                                {data.issues.slice(0, 2).map((issue: string, idx: number) => (
                                                    <Typography key={idx} variant="caption" color="error" display="block">
                                                        • {issue}
                                                    </Typography>
                                                ))}
                                                {data.issues.length > 2 && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        +{data.issues.length - 2} more issues
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="success.main">
                                                ✓ No issues found
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* Critical Issues */}
            {report.critical_issues && report.critical_issues.length > 0 && (
                <Card sx={{ mb: 3, borderLeft: '4px solid #f44336' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <ErrorIcon color="error" sx={{ mr: 1 }} />
                            <Typography variant="h6" color="error">
                                Critical Issues (Score &lt; 60)
                            </Typography>
                        </Box>
                        {report.critical_issues.map((issue: any, idx: number) => (
                            <Accordion key={idx}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                                        {issue.category} - Score: {issue.score.toFixed(1)}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {issue.issues.map((item: string, i: number) => (
                                        <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                                            • {item}
                                        </Typography>
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Warnings */}
            {report.warnings && report.warnings.length > 0 && (
                <Card sx={{ mb: 3, borderLeft: '4px solid #ff9800' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <WarningIcon color="warning" sx={{ mr: 1 }} />
                            <Typography variant="h6" color="warning.main">
                                Warnings (Score 60-79)
                            </Typography>
                        </Box>
                        {report.warnings.map((warning: any, idx: number) => (
                            <Accordion key={idx}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                                        {warning.category} - Score: {warning.score.toFixed(1)}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {warning.issues.map((item: string, i: number) => (
                                        <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                                            • {item}
                                        </Typography>
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Recommendations */}
            {report.recommendations && report.recommendations.length > 0 && (
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">
                                Recommendations
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Priority</TableCell>
                                        <TableCell>Category</TableCell>
                                        <TableCell>Issue</TableCell>
                                        <TableCell>Recommended Action</TableCell>
                                        <TableCell>Impact</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {report.recommendations.map((rec: any, idx: number) => (
                                        <TableRow key={idx}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {getPriorityIcon(rec.priority)}
                                                    <Typography variant="body2" sx={{ ml: 1, textTransform: 'capitalize' }}>
                                                        {rec.priority}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{rec.category}</TableCell>
                                            <TableCell>{rec.issue}</TableCell>
                                            <TableCell>{rec.action}</TableCell>
                                            <TableCell>
                                                <Typography variant="caption" color="textSecondary">
                                                    {rec.impact}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* Footer */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                    Report generated: {new Date(report.timestamp).toLocaleString()}
                    {qualityData?.data?.cached && ' (Cached)'}
                </Typography>
            </Box>
        </Box>
    );
};

export default EnrollmentDataQuality;
